import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { MessageSquare, RefreshCw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const STATUS_TONE = {
  sent: "default", delivered: "default", queued: "secondary", sending: "secondary",
  failed: "destructive", undelivered: "destructive", not_configured: "destructive", received: "default",
};

export function SmsStatusPanel({ leadId, canRetry }) {
  const [data, setData] = useState(undefined);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/sms/leads/${leadId}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [leadId]);
  useEffect(() => { load(); }, [load]);

  if (data === undefined || data === null) return null;
  // only show for campaign leads that have SMS activity or eligibility info
  const isCampaignLead = data.eligibility_reason !== "not_campaign_lead" || (data.messages || []).length > 0;
  if (!isCampaignLead) return null;

  const retry = async () => {
    setBusy(true);
    try { await api.post(`/sms/leads/${leadId}/retry`); toast.success("Retry sent"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const last = data.sms_ack_status || (data.eligible ? "eligible" : data.eligibility_reason);
  const inbound = (data.messages || []).filter((m) => m.direction === "inbound");

  return (
    <div className="mb-4 rounded-lg border bg-card p-3" data-testid="case-sms-panel">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold"><MessageSquare className="h-4 w-4 text-muted-foreground" /> SMS</div>
        {canRetry && data.can_retry && (
          <Button size="sm" variant="outline" onClick={retry} disabled={busy} data-testid="case-sms-retry">
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Retry
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div className="text-muted-foreground">Ack status</div>
        <div data-testid="case-sms-status"><Badge variant={STATUS_TONE[last] || "secondary"}>{last || "—"}</Badge></div>
        <div className="text-muted-foreground">SMS consent</div>
        <div>{data.consent_sms_permission ? "Yes" : "No"}</div>
        <div className="text-muted-foreground">Opted out</div>
        <div>{data.opt_out ? "Yes" : "No"}{data.dnc ? " · DNC" : ""}</div>
        <div className="text-muted-foreground">Quality stage</div>
        <div>{data.quality_stage || "—"}</div>
      </div>
      {(data.messages || []).length > 0 && (
        <div className="mt-3 space-y-1.5 border-t pt-2">
          {data.messages.map((m, i) => (
            <div key={i} className="text-[11px]" data-testid={`case-sms-msg-${i}`}>
              <span className={m.direction === "inbound" ? "text-emerald-600 font-medium" : "text-muted-foreground"}>
                {m.direction === "inbound" ? "← in" : "→ out"} · {m.status}
              </span>
              <span className="ml-2">{m.body}</span>
              {m.error_description && <span className="ml-1 text-red-500">({m.error_description})</span>}
            </div>
          ))}
        </div>
      )}
      {inbound.length > 0 && <div className="mt-2 text-[11px] font-medium text-emerald-600">Lead responded — follow up.</div>}
    </div>
  );
}
